import {registerPlugin} from './modules/registerPlugin'
import {createManualPromise} from '../utils/createManualPromise'
import {loadScript, loadStyle} from './modules/loadAsset'

const PluginLoader = function(args) {
    /*
     * Private member variables
     */
    const _configurator = args.configurator
    const _pluginLoaded = args.pluginLoaded // Callback when a plugin loaded

    /*
     * Public interface
     */
    return Object.freeze({
        load: _load
    })

    /**
     * Load all external plugins
     */
    async function _load() {
        const cfg = _configurator.getConfiguration()

        const enabledPlugins = cfg.plugins
            .filter(({enabled}) => enabled)
            .map((pluginConfig) => {
                return {
                    config: pluginConfig, // Plugin config to configure plugin with
                    ...createManualPromise() // Resolves when plugin registers
                }
            })

        try {
            _configurator.expose('registerPlugin', pluginPackage => {
                registerPlugin(pluginPackage, enabledPlugins, _pluginLoaded)
            })

            const responses = await _loadEnabledPluginsResources(enabledPlugins)
            const pluginPromises = enabledPlugins.map(({promise}) => promise)

            await _waitForPromises(pluginPromises, 5000)

            _configurePlugins(responses, enabledPlugins)
        } catch (ex) {
            console.error(ex)
            throw(ex)
        }
    }

    /**
     * Loop over all enabled plugins and call configure, fail if any mandatory
     * plugin has failed loading or timed out or not registered with in timeout.
     *
     * @param {array} resourceResponses
     * @param {array} enabledPlugins
     */
    function _configurePlugins(resourceResponses, enabledPlugins) {
        const missingMandatoryPlugins = []
        enabledPlugins.forEach(plugin => {
            const pluginResource = resourceResponses.find(r => r.name === plugin.config.name)
            if (plugin.config.mandatory && (pluginResource.status !== 'loaded' || !plugin.package)) {
                missingMandatoryPlugins.push(plugin.config.name)
            }
        })

        if (missingMandatoryPlugins.length) {
            throw new Error(`Failed loading plugins ${missingMandatoryPlugins}`)
        }

        enabledPlugins.forEach(plugin => {
            _configurePlugin(
                plugin,
                resourceResponses.find(r => r.name === plugin.config.name)
            )
        })
    }

    /**
     * Call a plugins configure function which initalizes the plugin
     *
     * @param       {Object} plugin   Plugin object with config etc
     * @param       {Object} resource Plugin resource object
     */
    function _configurePlugin(plugin, resource) {
        try {
            if (resource.status === 'loaded' && plugin.package) {
                plugin.package.configure(_configurator, plugin.config)
            }
        } catch (ex) {
            const msg = `Failed configuring mandatory plugin <${plugin.package.name}>: ${ex.message}`
            if (plugin.config.mandatory) {
                throw new Error(msg)
            }
            else {
                console.warn(msg)
            }
        }
    }

    /**
     * Loop over all enabled plugins and initiate loading of all unique
     * resources (plugin names) needed for those enabled plugins.
     *
     * @param   {array} enabledPlugins - Array of enabled plugins
     * @return  {Promise<array>} Array of Promises
     */
    function _loadEnabledPluginsResources(enabledPlugins) {
        const isLoading = []
        const resources = []

        enabledPlugins.forEach(plugin => {
            const {name} = plugin.config

            if (!isLoading.includes(name)) {
                isLoading.push(name)
                resources.push(_loadResources(plugin.config))
            }
        })

        return Promise.all(resources)
    }

    /**
     * Load all resources for one unique plugin
     * @param {object} pluginConfig Plugin configuration object
     * @return {Promise}
     */
    function _loadResources(pluginConfig) {
        const resources = [
            loadScript(pluginConfig),
            loadStyle(pluginConfig)
        ]

        return new Promise(resolve => {
            let resolved = false
            Promise.all(resources)
                .then(() => {
                    if (!resolved) {
                        resolve({name: pluginConfig.name, status: 'loaded'})
                    }
                })
                .catch(() => {
                    resolve({name: pluginConfig.name, status: 'failed'})
                })

            // Setup timeout for loading resources
            setTimeout(() => {
                resolved = true
                resolve({name: pluginConfig.name, status: 'timeout'})
            }, _configurator.getPluginLoadTimeout())
        })
    }

    /**
     * Wait for a maximum of <timeout> ms for all promises. The returned
     * promise will resolve, either when all plugins have registered or
     * when the timeout is hit.
     *
     * @param       {[Promise]} promises Array of promises to wait for
     * @param       {int} timeout Timeout to wait
     * @return      {Promise}
     */
    function _waitForPromises(promises, timeout) {

        return new Promise(resolve => {

            const loadTimeout = setTimeout(() => {
                console.warn(`Timed out when waiting for all plugins to register`)
                return resolve([])
            }, timeout)

            return Promise.all(promises)
                .then((res) => {
                    clearTimeout(loadTimeout)
                    return resolve(res)
                })
                .catch(ex => {
                    console.warn(`An error occurred when waiting for plugins to register: ${ex.message}`)
                    return resolve([])
                })
        })
    }
}

export {PluginLoader}
